import Session from '../auth/Session';
import { authFetch } from './apiClient';

const BASE = 'api/Quote';

// Sends eligibility form data to backend, which proxies to NormanMax
export async function createQuote(formData) {
    const body = {
        // effectiveDate: formData.EffectiveDate,         // YYYY-MM-DD string
        coverageLimit: Number(formData.CoverageAmount),
        ownerType: formData.OwnerType,
        firstName: formData.FirstName,
        lastName: formData.LastName,
        email: formData.UserName,
        phone: formData.PhoneNumberPrimary,
        address1: formData.TargetAddressOne,
        address2: formData.TargetAddressTwo || '',
        city: formData.TargetCity,
        state: formData.TargetState,
        zip: formData.TargetZipCode,
        country: 'USA',
        latitude: formData.TargetLat,
        longitude: formData.TargetLng,
    };

    const response = await authFetch(`${BASE}/CreateQuote`, {
        method: 'POST',
        body: JSON.stringify(body),
    });

    if (response.status === 422) {
        const err = new Error('OUTSIDE_COVERAGE_AREA');
        err.code = 'OUTSIDE_COVERAGE_AREA';
        throw err;
    }

    if (!response.ok) {
        const err = new Error(`CreateQuote failed: ${response.status}`);
        err.code = 'API_ERROR';
        err.status = response.status;
        throw err;
    }

    return response.json();
}

// Sends the selected trigger type + additional party info to issue the policy
export async function issuePolicy(payload) {
    // Map camelCase frontend payload to PascalCase DTO field names
    const body = {
        QuoteNumber: payload.quoteNumber,
        SelectedTriggerType: payload.selectedTriggerType,
        ProducingAgent: payload.producingAgent
            ? {
                Name: payload.producingAgent.name,
                EmailAddress: payload.producingAgent.emailAddress,
              }
            : null,
        AdditionalParties: payload.additionalParties?.map((p) => ({
            Type: p.type,
            Name: p.name,
            Address: p.address || '',
            City: p.city || '',
            State: p.state || '',
            ZipCode: p.zipCode || '',
            Country: p.country || 'US',
        })) || [],
        MailingSameAsProperty: payload.mailingSameAsProperty,
        MailingAddressOne: payload.mailingAddressOne,
        MailingCity: payload.mailingCity,
        MailingState: payload.mailingState,
        MailingZipCode: payload.mailingZipCode,
        TargetAddressOne: payload.targetAddressOne,
        TargetCity: payload.targetCity,
        TargetState: payload.targetState,
        TargetZipCode: payload.targetZipCode,
    };
 
    const response = await authFetch(`${BASE}/IssuePolicy`, {
        method: 'POST',
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        const err = new Error(`IssuePolicy failed: ${response.status}`);
        err.code = 'API_ERROR';
        err.status = response.status;
        throw err;
    }

    return response.json();
}

// Called ONCE after createQuote succeeds and we have a QuoteNumber.
export async function saveQuote(formData) {
    const response = await authFetch(`${BASE}/SaveQuote`, {
        method: 'POST',
        body: JSON.stringify(buildQuoteDTO(formData)),
    });

    if (!response.ok) {
        const err = new Error(`SaveQuote failed: ${response.status}`);
        err.code = 'API_ERROR';
        throw err;
    }

    return response.json();
}
 
// Called on each "Continue" click after the initial save.
export async function updateQuote(quoteId, formData) {
    const response = await authFetch(`${BASE}/UpdateQuote/${quoteId}`, {
        method: 'PUT',
        body: JSON.stringify(buildQuoteDTO(formData)),
    });

    if (!response.ok) {
        const err = new Error(`UpdateQuote failed: ${response.status}`);
        err.code = 'API_ERROR';
        throw err;
    }

    return response.json();
}
 
// Creates a Policy row after issuePolicy succeeds.
export async function savePolicy(formData, issuePolicyResponse) {
    const body = {
        Email: formData.UserName,
        PolicyNumber: issuePolicyResponse.policy_number,
        InvoiceNumber: issuePolicyResponse.invoice_number,
        TriggerType: issuePolicyResponse.trigger_type,
        EffectiveDate: issuePolicyResponse.effective_date,
        ExpiryDate: issuePolicyResponse.expiry_date,
        TotalPremium: issuePolicyResponse.total_premium,
        CoverageLimit: issuePolicyResponse.coverage_limit,
    };
 
    const response = await authFetch(`${BASE}/SavePolicy`, {
        method: 'POST',
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        const err = new Error(`SavePolicy failed: ${response.status}`);
        err.code = 'API_ERROR';
        throw err;
    }

    return response.json();
}
 
// Shared DTO builder
// Builds the QuoteDTO shape the backend expects.
// Used by both saveQuote and updateQuote so the shape is always consistent.
function buildQuoteDTO(formData) {
    return {
        CurrentStep: formData.currentStep,
        Email: formData.UserName,
        FirstName: formData.FirstName,
        LastName: formData.LastName,
        PhoneNumberPrimary: formData.PhoneNumberPrimary,
        PhoneNumberSecondary: formData.PhoneNumberSecondary,
        IsReceivingCriticalMessages: formData.IsReceivingCriticalMessages,
        OwnerType: formData.OwnerType,
        TargetAddressOne: formData.TargetAddressOne,
        TargetAddressTwo: formData.TargetAddressTwo,
        TargetCity: formData.TargetCity,
        TargetState: formData.TargetState,
        TargetZipCode: formData.TargetZipCode,
        TargetLat: formData.TargetLat,
        TargetLng: formData.TargetLng,
        TargetAddressFormatted: formData.TargetAddressFormatted,
        TargetHomeStatus: formData.TargetHomeStatus,
        TargetHomeStatusOther: formData.TargetHomeStatusOther,
        ExposedHome: formData.ExposedHome,
        ExposedLandImprovements: formData.ExposedLandImprovements,
        ExposedHardscaping: formData.ExposedHardscaping,
        ExposedContents: formData.ExposedContents,
        ExposedValuables: formData.ExposedValuables,
        ExposedVehicles: formData.ExposedVehicles,
        ExposedLivingExpenses: formData.ExposedLivingExpenses,
        IsExposedValueOverMillion: formData.IsExposedValueOverMillion,
        CoverageAmount: Number(formData.CoverageAmount),
        EffectiveDate: formData.EffectiveDate,
        QuoteNumber: formData.QuoteNumber,
        SelectedTriggerType: formData.SelectedTriggerType,
        SelectedTotalPremium: formData.SelectedTotalPremium || 0,
        ContactAddressOne: formData.ContactAddressOne,
        ContactAddressTwo: formData.ContactAddressTwo,
        ContactCity: formData.ContactCity,
        ContactState: formData.ContactState,
        ContactZipCode: formData.ContactZipCode,
        ContactCountry: formData.ContactCountry,
        MailingSameAsProperty: formData.MailingSameAsProperty,
        HasAdditionalParties: formData.HasAdditionalParties,
        AdditionalPartyType: formData.AdditionalPartyType,
        AdditionalPartyFirstName: formData.AdditionalPartyFirstName,
        AdditionalPartyLastName: formData.AdditionalPartyLastName,
        AdditionalPartyName: formData.AdditionalPartyName,
        AdditionalPartyAddressOne: formData.AdditionalPartyAddressOne,
        AdditionalPartyAddressTwo: formData.AdditionalPartyAddressTwo,
        AdditionalPartyCity: formData.AdditionalPartyCity,
        AdditionalPartyState: formData.AdditionalPartyState,
        AdditionalPartyZipCode: formData.AdditionalPartyZipCode,
        AdditionalPartyCountry: formData.AdditionalPartyCountry,
    };
}